import React, { useRef, useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Fab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PhotoCamera, CachedOutlined, PhotoLibrary } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "../hooks";
import { useAppDispatch, useAppSelector } from "../app/hook";
import { userId } from "../modules/auth/selectors/userSelector";
import { setUserCameraId } from "../modules/auth/slices/userSlice";
import {
  isAlreadySet,
  otherUsersSettings,
} from "../modules/settings/components/utils";
import usePhotosDialogStyles from "./css/usePhotosDialogStyles";
import CommentDialog from "./CommentDialog";
import PhotoPreview from "./PhotoPreview";
import {
  startCamera,
  stopCamera,
  takePhoto,
  restartCamera,
} from "../utils/photosUtils";
import { handleUploadFile, handleSelectFile } from "../utils/photosFileUtils";
import { useUploadError } from "../hooks";
import Alert from "./Alert";
import { getErrorMessage } from "./form/formUtils";

export interface PhotoUploadProps {
  open: boolean;
  onClose: () => void;
  clientDbId: number;
  module?: string;
  operationCode: string;
  locale: string;
  onRefresh: () => void;
  rowNumber?: number;
}

const PhotosCamera: React.FC<PhotoUploadProps> = ({
  open,
  onClose,
  clientDbId,
  module,
  operationCode,
  locale,
  onRefresh,
  rowNumber,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [photo, setPhoto] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [hasComment, setHasComment] = useState(false);
  const { error, showError, clearError } = useUploadError();

  const { t } = useTranslation();
  const { classes } = usePhotosDialogStyles();

  const dispatch = useAppDispatch();
  const user = useAppSelector(userId);

  const [camerIdLS, setCameraIdLS] = useLocalStorage("cameraId", [
    { userId: user, cameraId: "" },
  ]);
  const [cameraId, setCameraId] = useState<string | undefined>();
  const [camerasList, setCamerasList] =
    useState<{ id: string; label: string }[]>();
  const [openCamerasList, setOpenCamerasList] = useState<null | HTMLElement>(
    null
  );
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const isCameraId = isAlreadySet(camerIdLS, user);
    if (isCameraId.length > 0) setCameraId(isCameraId[0].cameraId);
  }, [camerIdLS, user]);

  // kamera start/stop
  useEffect(() => {
    if (open) startCamera(videoRef, streamRef, cameraId);
    return () => stopCamera(videoRef, streamRef);
  }, [open, cameraId]);

  const handleClose = () => {
    stopCamera(videoRef, streamRef);
    onClose();
  };

  const handleTakePhoto = () => {
    takePhoto(
      videoRef,
      canvasRef,
      (img) => setPhoto(img),
      () => stopCamera(videoRef, streamRef)
    );
  };

  const handleRestartCamera = () => {
    restartCamera(setPhoto, videoRef, streamRef, cameraId);
  };

  const handleUpload = async () => {
    try {
      await handleUploadFile({
        photo,
        clientDbId,
        module,
        operationCode,
        rowNumber,
        comment,
        locale,
        onRefresh,
        onClose,
        setPhoto,
        setComment,
      });
    } catch (err: any) {
      const { message } = getErrorMessage(err);
      showError(message);
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    await handleSelectFile(
      event,
      (img) => setPhoto(img),
      () => stopCamera(videoRef, streamRef),
      showError
    );
  };

  const handleCameraList = async (event: any) => {
    setOpenCamerasList(event.currentTarget);
    const devices = await navigator.mediaDevices.enumerateDevices();
    const cameras = devices
      .filter((device) => device.kind === "videoinput")
      .map((device) => {
        return { id: device.deviceId, label: device.label };
      });
    setCamerasList(cameras);
  };

  const handleSelectedCamera = (index: number) => {
    if (camerasList && camerasList.length > 0 && camerasList[index]) {
      setCameraId(camerasList[index].id);
      dispatch(setUserCameraId(camerasList[index].id));

      const otherUsers = otherUsersSettings(camerIdLS, user);
      setCameraIdLS([
        ...otherUsers,
        { userId: user, cameraId: camerasList[index].id },
      ]);
    }
    setOpenCamerasList(null);
  };

  return (
    <>
      {/* Pagrindinis dialogas */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="body"
        className={classes.dialogRoot}
      >
        <div className={classes.header}>
          {photo
            ? t("addPhoto", "Pridėti nuotrauką")
            : t("capturePhoto", "Fotografuoti")}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* kamera rėžimas */}
        {!photo && (
          <DialogContent className={classes.cameraContent}>
            <Box
              sx={{
                padding: "10px 10px 0 10px",
                backgroundColor: "#dadade",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: "calc(100vh - 160px - 13px)",
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <Fab
                  onClick={handleCameraList}
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: "20px",
                    right: "20px",
                    backgroundColor: "white",
                    color: "black",
                    border: "1px solid #ccc",
                    "&:hover": { backgroundColor: "#eee" },
                  }}
                >
                  <CachedOutlined />
                </Fab>
              </Box>
            </Box>

            <div className={classes.cameraButtonWrapper}>
              <IconButton
                component="label"
                sx={{
                  position: "absolute",
                  left: "20%",
                  transform: "translateX(-50%)",
                  color: "#757575",
                }}
              >
                <PhotoLibrary />
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileSelect}
                />
              </IconButton>

              <IconButton
                onClick={handleTakePhoto}
                sx={{
                  backgroundColor: "rgb(0, 172, 110)",
                  color: "white",
                  "&:hover": { backgroundColor: "rgb(0, 125, 80)" },
                }}
              >
                <PhotoCamera />
              </IconButton>
            </div>
          </DialogContent>
        )}

        {/* Kamerų pasirinkimo meniu */}
        {Boolean(openCamerasList) && (
          <Menu
            open={Boolean(openCamerasList)}
            anchorEl={openCamerasList}
            onClose={() => setOpenCamerasList(null)}
          >
            {camerasList?.map((device, index) => (
              <MenuItem
                key={device.id}
                onClick={() => handleSelectedCamera(index)}
              >
                {device.label || `Camera ${index + 1}`}
              </MenuItem>
            ))}
          </Menu>
        )}

        {/* Peržiūros rėžimas */}
        {photo && (
          <PhotoPreview
            photo={photo}
            hasComment={hasComment}
            onAddComment={() => setCommentOpen(true)}
            onUpload={handleUpload}
            onRestart={handleRestartCamera}
            onCancel={handleClose}
            t={t}
          />
        )}

        <canvas ref={canvasRef} style={{ display: "none" }} />
      </Dialog>

      {/* Komentarų dialogas */}
      <CommentDialog
        open={commentOpen}
        hasComment={hasComment}
        comment={comment}
        onClose={() => setCommentOpen(false)}
        onSave={() => {
          setHasComment(true);
          setCommentOpen(false);
        }}
        onChange={setComment}
        inputRef={commentInputRef}
        t={t}
      />
      <Alert
        isOpen={!!error}
        onClose={clearError}
        message={error || ""}
        iconColor="error"
      />
    </>
  );
};

export default PhotosCamera;
